import { useLazyQuery } from '@apollo/client'
import {
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    Typography,
} from '@mui/material'
import Container from '@mui/material/Container'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import { DatePicker } from '@mui/x-date-pickers'
import IntakeCard from 'candidate/components/IntakeCard'
import { useEffect, useState } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { GetCompanyOffices } from 'recruiter/graphql/companies.gql'
import NextButton from 'shared/components/NextButton'
import NonFormikPhoneInput from 'shared/components/NonFormikPhoneInput'
import { getPxAsRem } from 'shared/utils'

import Autocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import type { ChoiceBucket } from 'candidate/models/intake'
import type { Dispatch, FC, SetStateAction } from 'react'
import CompaniesAutocomplete from 'shared/components/CompaniesAutocomplete/CompaniesAutocomplete'
import { toMonthDate } from 'shared/utils/dateFormatter'

export interface EmploymentAnswer {
    company: (ChoiceBucket & { id: string }) | undefined
    companyOffice: ChoiceBucket | undefined
    workTitle: string
    startDate: string
    endDate: string
    isCurrent: boolean
    industries: ChoiceBucket[] | undefined
    sectors: ChoiceBucket[] | undefined
    workEmail: string
    workPhone: string
}

function isFormValid(response: EmploymentAnswer): boolean {
    return !!(
        response.company &&
        response.workTitle !== '' &&
        response.startDate !== '' &&
        (response.industries?.length ?? 0) > 0 &&
        (response.sectors?.length ?? 0) > 0 &&
        (!response.workPhone || isValidPhoneNumber(response.workPhone))
    )
}

const EMPTY_EMPLOYMENT_ANSWER: EmploymentAnswer = {
    company: undefined,
    companyOffice: undefined,
    workTitle: '',
    startDate: '',
    endDate: '',
    isCurrent: true,
    industries: undefined,
    sectors: undefined,
    workEmail: '',
    workPhone: '',
}

interface EmploymentFormFieldsProps {
    response: EmploymentAnswer
    onResponseChange: (response: EmploymentAnswer) => void
    workTitles: string[]
    industryChoices: ChoiceBucket[]
    sectorChoices: ChoiceBucket[]
    idPrefix?: string
    initialAnswer?: EmploymentAnswer
}

const EmploymentFormFields: FC<EmploymentFormFieldsProps> = ({
    response,
    onResponseChange,
    workTitles,
    industryChoices,
    sectorChoices,
    idPrefix = '',
    initialAnswer,
}) => {
    const [companyOffices, setCompanyOffices] = useState<ChoiceBucket[]>([])

    // Set up an on-demand query for company office locations.
    const [getCompanyOffices, { loading: isOfficeLocationDataLoading }] =
        useLazyQuery(GetCompanyOffices, {
            onCompleted: (data) => {
                const companyOfficeChoices =
                    data.company.companyOffices?.map(({ officeLocation }) => ({
                        choice: officeLocation?.value ?? '',
                    })) ?? []

                setCompanyOffices(companyOfficeChoices)
            },
        })

    // Pre-fill: fetch office locations for the initial company
    useEffect(() => {
        if (initialAnswer?.company) {
            getCompanyOffices({
                variables: { companyId: initialAnswer.company.id },
            })
        }
    }, [initialAnswer, getCompanyOffices])

    return (
        <Container
            style={{
                display: 'inline-block',
                alignContent: 'left',
                marginBottom: getPxAsRem(36),
                marginTop: getPxAsRem(16),
                padding: '0',
                width: '100%',
                maxWidth: '100%',
            }}
        >
            <CompaniesAutocomplete
                inCandidatePortal
                sx={{ marginBottom: getPxAsRem(24) }}
                value={
                    response.company
                        ? {
                              id: response.company.id,
                              value: response.company.choice,
                          }
                        : null
                }
                onChange={(e, nextValue) => {
                    if (nextValue?.id && nextValue?.value) {
                        onResponseChange({
                            ...response,
                            company: {
                                id: nextValue.id,
                                choice: nextValue.value,
                            },
                        })
                    }
                    // fetch officeLocations for the selected company
                    if (nextValue?.id) {
                        getCompanyOffices({
                            variables: { companyId: nextValue?.id },
                        })
                    }
                }}
            />

            <FormControl
                fullWidth
                sx={{
                    marginBottom: getPxAsRem(24),

                    '&:has(.Mui-disabled)': {
                        opacity: 0.5,
                        cursor: 'not-allowed',
                    },
                }}
            >
                <InputLabel id={`${idPrefix}office_label`}>
                    Company Office
                </InputLabel>

                <Select
                    id={`${idPrefix}company_office`}
                    label="Company Office"
                    labelId={`${idPrefix}company_office_label`}
                    value={response.companyOffice?.choice || ''}
                    disabled={
                        !response.company ||
                        isOfficeLocationDataLoading ||
                        companyOffices.length === 0
                    }
                    onChange={(e: SelectChangeEvent<string>) => {
                        const companyOffice = companyOffices.find(
                            (choiceBucket) =>
                                choiceBucket.choice === e.target.value
                        )

                        onResponseChange({
                            ...response,
                            companyOffice,
                        })
                    }}
                >
                    {companyOffices.map((choiceBucket, index) => (
                        <MenuItem
                            key={`${choiceBucket.choice}_${index}`}
                            value={choiceBucket.choice}
                        >
                            {choiceBucket.choice}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl fullWidth style={{ marginBottom: getPxAsRem(24) }}>
                <InputLabel id={`${idPrefix}work_title`}>Work Title</InputLabel>
                <Select
                    id={`${idPrefix}title`}
                    labelId={`${idPrefix}title_label`}
                    label="Work Title"
                    value={response.workTitle}
                    onChange={(e) => {
                        const workTitle =
                            workTitles.find(
                                (title: string) => title === e.target.value
                            ) || ''
                        onResponseChange({ ...response, workTitle })
                    }}
                >
                    {workTitles.map((title: string) => (
                        <MenuItem key={title} value={title}>
                            {title}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControlLabel
                checked={response.isCurrent}
                onChange={(_event, checked) => {
                    onResponseChange({
                        ...response,
                        isCurrent: checked,
                        endDate: checked ? '' : response.endDate,
                    })
                }}
                sx={{ marginBottom: getPxAsRem(24) }}
                control={<Checkbox />}
                label={
                    <Typography sx={{ userSelect: 'none' }}>
                        This is my current opportunity
                    </Typography>
                }
            />

            <div
                style={{
                    display: 'flex',
                    marginBottom: getPxAsRem(24),
                    width: '100%',
                }}
            >
                <FormControl
                    fullWidth
                    style={{
                        marginRight: !response.isCurrent
                            ? getPxAsRem(16)
                            : undefined,
                        width: !response.isCurrent ? '48%' : '100%',
                    }}
                >
                    <DatePicker
                        format="MM/yyyy"
                        value={toMonthDate(response.startDate, 'dateobject')}
                        onChange={(value: Date | null) => {
                            onResponseChange({
                                ...response,
                                startDate: toMonthDate(value, 'MM-yyyy'),
                            })
                        }}
                        views={['month', 'year']}
                        label="Start Date"
                    />
                </FormControl>

                {!response.isCurrent && (
                    <FormControl fullWidth style={{ width: '50%' }}>
                        <DatePicker
                            format="MM/yyyy"
                            value={toMonthDate(response.endDate, 'dateobject')}
                            onChange={(value: Date | null) => {
                                onResponseChange({
                                    ...response,
                                    endDate: toMonthDate(value, 'MM-yyyy'),
                                })
                            }}
                            views={['month', 'year']}
                            label="End Date"
                        />
                    </FormControl>
                )}
            </div>

            <div
                style={{
                    display: 'flex',
                    marginBottom: getPxAsRem(24),
                    width: '100%',
                }}
            >
                <FormControl
                    fullWidth
                    style={{
                        marginRight: getPxAsRem(16),
                        width: '48%',
                    }}
                >
                    <Autocomplete
                        multiple
                        id={`${idPrefix}industry`}
                        options={industryChoices}
                        getOptionLabel={(option) => option.choice}
                        isOptionEqualToValue={(option, value) =>
                            option.choice === value.choice
                        }
                        value={response.industries || []}
                        onChange={(event, newValue) => {
                            onResponseChange({
                                ...response,
                                industries: newValue,
                            })
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Industries"
                                placeholder="Select industries"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option.choice}
                                    {...getTagProps({ index })}
                                    key={option.choice}
                                />
                            ))
                        }
                    />
                </FormControl>
                <FormControl fullWidth style={{ width: '50%' }}>
                    <Autocomplete
                        multiple
                        id={`${idPrefix}sector`}
                        options={sectorChoices}
                        getOptionLabel={(option) => option.choice}
                        isOptionEqualToValue={(option, value) =>
                            option.choice === value.choice
                        }
                        value={response.sectors || []}
                        onChange={(event, newValue) => {
                            onResponseChange({
                                ...response,
                                sectors: newValue,
                            })
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Sectors"
                                placeholder="Select sectors"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option.choice}
                                    {...getTagProps({ index })}
                                    key={option.choice}
                                />
                            ))
                        }
                    />
                </FormControl>
            </div>
            <div
                style={{
                    display: 'flex',
                    marginBottom: getPxAsRem(24),
                    width: '100%',
                }}
            >
                <FormControl
                    fullWidth
                    style={{
                        marginRight: getPxAsRem(16),
                        width: '48%',
                    }}
                >
                    <TextField
                        id={`${idPrefix}work_email`}
                        label="Work Email"
                        value={response.workEmail}
                        onChange={(e) => {
                            onResponseChange({
                                ...response,
                                workEmail: e.target.value,
                            })
                        }}
                    />
                </FormControl>
                <FormControl fullWidth style={{ width: '50%' }}>
                    <NonFormikPhoneInput
                        id={`${idPrefix}phone`}
                        size="small"
                        value={response.workPhone}
                        error={
                            response.workPhone !== '' &&
                            !isValidPhoneNumber(response.workPhone)
                                ? true
                                : false
                        }
                        helperText={
                            response.workPhone !== '' &&
                            !isValidPhoneNumber(response.workPhone)
                                ? 'Please provide valid phone no.'
                                : ''
                        }
                        label="Work Phone"
                        handleInputChange={(value) => {
                            onResponseChange({
                                ...response,
                                workPhone: value || '',
                            })
                        }}
                    />
                </FormControl>
            </div>
        </Container>
    )
}

interface Props {
    answer?: EmploymentAnswer
    callback: Dispatch<any>
    workTitles: string[]
    industryChoices: ChoiceBucket[]
    sectorChoices: ChoiceBucket[]
    prompt: string
    skipCallback?: Dispatch<SetStateAction<string>>
    skipText?: string
}

const EmploymentQuestion: FC<Props> = ({
    answer,
    callback,
    workTitles,
    industryChoices,
    sectorChoices,
    prompt,
    skipCallback,
    skipText,
}: Props) => {
    const [response, setResponse] = useState<EmploymentAnswer>({
        company: answer?.company || undefined,
        companyOffice: answer?.companyOffice || undefined,
        workTitle: answer?.workTitle || '',
        startDate: answer?.startDate || '',
        endDate: answer?.endDate || '',
        isCurrent: answer?.isCurrent ?? true,
        industries: answer?.industries || undefined,
        sectors: answer?.sectors || undefined,
        workEmail: answer?.workEmail || '',
        workPhone: answer?.workPhone || '',
    })
    const [newResponse, setNewResponse] = useState<EmploymentAnswer>(
        EMPTY_EMPLOYMENT_ANSWER
    )

    // Pre-fill the form with any existing answers
    useEffect(() => {
        if (!answer) return
        setResponse(answer)
    }, [answer])

    const hasEndDate = response.endDate !== ''
    const firstFormValid = isFormValid(response)
    const disabled = hasEndDate
        ? !firstFormValid || !isFormValid(newResponse)
        : !firstFormValid

    const submitData = () => {
        if (hasEndDate) {
            callback({ response, newResponse })
        } else {
            callback({ response })
        }
    }

    return (
        <IntakeCard
            question={prompt}
            skipCallback={skipCallback}
            skipText={skipText}
        >
            <EmploymentFormFields
                response={response}
                onResponseChange={setResponse}
                workTitles={workTitles}
                industryChoices={industryChoices}
                sectorChoices={sectorChoices}
                initialAnswer={answer}
            />

            {hasEndDate && (
                <>
                    <Divider sx={{ marginBottom: getPxAsRem(24) }}>
                        <Typography variant="subtitle1" color="text.secondary">
                            New Current Position
                        </Typography>
                    </Divider>

                    <EmploymentFormFields
                        response={newResponse}
                        onResponseChange={setNewResponse}
                        workTitles={workTitles}
                        industryChoices={industryChoices}
                        sectorChoices={sectorChoices}
                        idPrefix="new_"
                    />
                </>
            )}

            <NextButton disabled={disabled} onClickHandler={submitData} />
        </IntakeCard>
    )
}

export default EmploymentQuestion
